import useSelectionFunctions from '@/hooks/useSelectionFunctions';
import type {
  CommoditiesTableRowType,
  CommoditiesTableSectionType
} from '@/pages/Markets/MarketsComponents/types';
import { createSelectedSymbol } from '@/slices/symbolSelectorSlice';
import type { SelectedSymbol } from '@/types/symbols';

const useCommoditiesSelection = (sections: CommoditiesTableSectionType[]) => {
  const {
    addSymbolsArrayToSelection,
    addAllSymbolsToSelection,
    allSymbolsSelected,
    removeSymbolFromSelection,
    removeSymbolsFromSelection
  } = useSelectionFunctions();

  const getSymbolsFrom = (row: CommoditiesTableRowType) => {
    const symbols: Array<SelectedSymbol> = [];
    const last = createSelectedSymbol(row.symbol.key);
    const diff = createSelectedSymbol(row.diffSymbol.key);
    if (last.Code) {
      symbols.push(last);
    }
    if (diff.Code) {
      symbols.push(diff);
    }
    return symbols;
  };

  const isSectionSelected = (rows: CommoditiesTableRowType[]): boolean => {
    const symbols = rows.filter(row => row.last || row.diff).flatMap(row => getSymbolsFrom(row));
    if (symbols.length < 1) return false;
    return allSymbolsSelected(symbols);
  };

  const toggleSectionInSelection = (rows: CommoditiesTableRowType[]) => {
    if (isSectionSelected(rows)) removeSectionFromSelection(rows);
    else addSectionToSelection(rows);
  };

  const removeSectionFromSelection = (rows: CommoditiesTableRowType[]) => {
    for (const row of rows) {
      const last = createSelectedSymbol(row.symbol.key);
      const diff = createSelectedSymbol(row.diffSymbol.key);
      removeSymbolFromSelection(last);
      removeSymbolFromSelection(diff);
    }
  };

  const addSectionToSelection = (rows: CommoditiesTableRowType[]) => {
    const symbols = rows.flatMap(row => getSymbolsFrom(row));
    addSymbolsArrayToSelection(symbols);
  };

  const toggleCodeInSelection = (row: CommoditiesTableRowType, codeSelected: boolean): void => {
    const symbols = getSymbolsFrom(row);
    if (codeSelected) {
      for (const symbol of symbols) {
        removeSymbolFromSelection(symbol);
      }
    } else {
      const symbols = getSymbolsFrom(row);
      addSymbolsArrayToSelection(symbols);
    }
  };

  const getAllSymbolsInTab = () =>
    sections.flatMap(({ rows }) => {
      return rows
        .filter(row => row.last || row.diff)
        .flatMap(row => {
          return getSymbolsFrom(row);
        });
    });

  const addAllCodesToSelection = () => {
    const newSymbols = getAllSymbolsInTab();
    addAllSymbolsToSelection(newSymbols);
  };

  const clearAllCodesFromSelection = () => {
    removeSymbolsFromSelection(getAllSymbolsInTab());
  };

  const allCodesSelected = () => {
    if (sections?.length < 1) return false;
    return sections?.every(section => {
      return isSectionSelected(section.rows);
    });
  };

  return {
    toggleSectionInSelection,
    allCodesSelected,
    addAllCodesToSelection,
    clearAllCodesFromSelection,
    isSectionSelected,
    toggleCodeInSelection
  };
};

export default useCommoditiesSelection;
